/**
 * Studio localisation entrypoint. `initStudioLocale()` boots the shared
 * react-i18next runtime with Studio's bundled catalogs — call once at startup
 * (main.tsx) alongside initTheme(). The user's persisted `preferredLocale`
 * (from /me) can be passed as the override so the choice follows them across
 * devices; absent that, detection falls back to localStorage → navigator → EN.
 */

import { createArchI18n, type Resource } from "@archlang/i18n";
import { viewerResources, VIEWER_NAMESPACES } from "@archlang/app-shell/i18n";
import { resources, STUDIO_DEFAULT_NS, STUDIO_NAMESPACES } from "./resources";

/**
 * Merge two `{ lng: { ns: { key: val } } }` resource trees. Namespaces are
 * unioned; an overlapping namespace (only `common`) has its keys merged with
 * the second tree winning on a key clash.
 */
function mergeResources(base: Resource, over: Resource): Resource {
  const out: Record<string, Record<string, Record<string, unknown>>> = {};
  for (const lng of new Set([...Object.keys(base), ...Object.keys(over)])) {
    const b = (base[lng] ?? {}) as Record<string, Record<string, unknown>>;
    const o = (over[lng] ?? {}) as Record<string, Record<string, unknown>>;
    const merged: Record<string, Record<string, unknown>> = { ...b };
    for (const ns of Object.keys(o)) merged[ns] = { ...(b[ns] ?? {}), ...o[ns] };
    out[lng] = merged;
  }
  return out as Resource;
}

/**
 * Initialise Studio chrome localisation. Idempotent. Studio embeds app-shell
 * components (the editor mounts the board + WorkspaceNav), so it loads the
 * viewer namespaces alongside its own — otherwise that embedded chrome renders
 * raw keys.
 */
export function initStudioLocale(preferredLocale?: string | null): void {
  createArchI18n({
    resources: mergeResources(viewerResources, resources),
    ns: [...new Set([...VIEWER_NAMESPACES, ...STUDIO_NAMESPACES])],
    defaultNS: STUDIO_DEFAULT_NS,
    localeOverride: preferredLocale,
  });
}

export { apiErrorMessage, type ApiErrorLike } from "./apiError";

export {
  useTranslation,
  Trans,
  useLocale,
  setLocale,
  getLocale,
  LOCALES,
  DEFAULT_LOCALE,
  type LocaleId,
} from "@archlang/i18n";
